import {
  type CoreDslFormOperator,
  type FilterType,
  includeFilterTypes,
  inFilterTypes,
  overlapFilterTypes,
  type PropertyType,
} from '@salescore/core'
import { Array } from 'effect'

const arrayMembershipConditionTypes = [
  ...overlapFilterTypes,
  ...includeFilterTypes,
  ...inFilterTypes,
] as const satisfies ReadonlyArray<FilterType & CoreDslFormOperator>
export type ArrayMembershipConditionType = (typeof arrayMembershipConditionTypes)[number]

export const isArrayMembershipConditionType = (
  x: FilterType | CoreDslFormOperator,
): x is ArrayMembershipConditionType => Array.contains(arrayMembershipConditionTypes, x)

type TranslateFunction = (key: string) => string

export const getArrayConditionLabel = (t: TranslateFunction, conditionType: ArrayMembershipConditionType): string => {
  switch (conditionType) {
    case 'overlap': {
      return t(`次の要素を1つ以上含む`)
    }
    case 'not_overlap': {
      return t(`次の要素を1つも含まない`)
    }
    case 'include': {
      return t(`次の要素を全て含む`)
    }
    case 'not_include': {
      return t(`次の要素を全ては含まない`)
    }
    case 'in': {
      return t(`次の要素だけを含む`)
    }
    case 'not_in': {
      return t(`次の要素以外も含む`)
    }
  }
}

// CoreDslFormOperator 18 種の base ラベルの正本。ラベルを 1 つ変えるときの追従箇所を 1 つにするためここに集約する。
// array 専用の overlap / not_overlap は propertyType に依存せず常に改名ラベルを返すため、ここでも getArrayConditionLabel を通す。
// include/not_include は array 前提の文言(「次の要素を全て含む/含まない」)。string 等の項目もこの base に落ちるため、
// string の include も「次の要素を全て含む」と表示される(旧 OperatorLabelText からの既存挙動を踏襲)。
// なお絞り込みフォーム(SheetNavigation/filter/common.ts)だけは string の include/not_include を
// 「次の文字列を含む/含まない」に上書きしており、同じ string 項目でも画面によって文言が割れている。
// 統一には FilterType と CoreDslFormOperator という 2 つの演算子語彙の統合が要るため別 Issue で扱う。
export const getBaseOperatorLabel = (t: TranslateFunction, op: CoreDslFormOperator): string => {
  switch (op) {
    case 'in': {
      return t(`次のどれか`)
    }
    case 'not_in': {
      return t(`次のどれでもない`)
    }
    case '=': {
      return t(`等しい`)
    }
    case '!=': {
      return t(`異なる`)
    }
    case 'null': {
      return t(`入力なし`)
    }
    case 'not_null': {
      return t(`入力あり`)
    }
    case 'blank': {
      return t(`入力なし`)
    }
    case 'present': {
      return t(`入力あり`)
    }
    case 'include': {
      return t(`次の要素を全て含む`)
    }
    case 'not_include': {
      return t(`次の要素を全て含まない`)
    }
    case 'starts_with': {
      return t(`次の文字列から始まる`)
    }
    case 'not_starts_with': {
      return t(`次の文字列から始まらない`)
    }
    case 'overlap':
    case 'not_overlap': {
      return getArrayConditionLabel(t, op)
    }
    case '<': {
      return t(`より小さい`)
    }
    case '<=': {
      return t(`以下`)
    }
    case '>': {
      return t(`より大きい`)
    }
    case '>=': {
      return t(`以上`)
    }
  }
}

// array(複数選択)項目のときだけ array 帰属 6 演算子を改名ラベルにし、それ以外は base ラベル(getBaseOperatorLabel)にフォールバックする。
// propertyType が導出できない(undefined)保存済み条件では base ラベルに落ちる(= array 判定できない旧挙動を維持する)。
export const getOperatorLabel = (
  t: TranslateFunction,
  op: CoreDslFormOperator,
  propertyType: PropertyType | undefined,
): string => {
  if (propertyType === 'array' && isArrayMembershipConditionType(op)) {
    return getArrayConditionLabel(t, op)
  }
  return getBaseOperatorLabel(t, op)
}

// ハイライト/条件付き書式の演算子ラベルの正本。フォーム(CoreDslFormOperatorFormItem)と条件表示セル(SheetValueCell)が共有し、
// 同じ条件が画面によって別の言葉で表示されないようにする。
// getOperatorLabel の上に、date/datetime・string で型に即した上書きラベル(「より前」「より上位」等)を重ねる。
// t は呼び出し側で同じ行に並ぶ値ラベルと同じものを渡す(条件表示セルはグローバル i18next.t)。値ラベルと別の t を購読すると、
// 言語切替時の再描画タイミングがずれて 1 行に新旧言語が混在する。
// 上書きの無い型を default でまとめず全列挙するのは、PropertyType が増えたときに switch 網羅で compile error にするため。
export const getCoreDslFormOperatorLabel = (
  t: TranslateFunction,
  op: CoreDslFormOperator,
  propertyType: PropertyType | undefined,
): string => {
  switch (propertyType) {
    case undefined:
    case 'boolean':
    case 'numeric':
    case 'integer':
    case 'object':
    case 'time':
    case 'array': {
      return getOperatorLabel(t, op, propertyType)
    }
    case 'date':
    case 'datetime': {
      switch (op) {
        case '<': {
          return t(`より前`)
        }
        case '<=': {
          return t(`以前`)
        }
        case '>': {
          return t(`より後`)
        }
        case '>=': {
          return t(`以後`)
        }
        default: {
          return getOperatorLabel(t, op, propertyType)
        }
      }
    }
    case 'string': {
      switch (op) {
        case '<': {
          return t(`より上位`)
        }
        case '>': {
          return t(`より下位`)
        }
        default: {
          return getOperatorLabel(t, op, propertyType)
        }
      }
    }
  }
}
